<template>
    <div :class="embedded ? null : 'content-wrapper'">
        <div :class="embedded ? null : 'content'">
            <div class="connect-panel">
                <div class="section-title">
                    <div>
                        <span class="eyebrow">Авторизация канала</span>
                        <h2>Подключите Telegram</h2>
                        <p>Отсканируйте QR-код с рабочего номера телефона и подтвердите вход.</p>
                    </div>
                    <span class="badge">QR-код</span>
                </div>

                <ChannelSettingsForm
                    v-if="channelConnector && providerType && !isTransitioning"
                    :value="channelSettings"
                    :connector="channelConnector"
                    :providerType="providerType"
                    :isOnboarding="true"
                    :appCreated="true"
                    :inSidebar="!redirectOnConnect"
                    @input="channelSettings = $event"
                    @saved="onChannelConnected"
                />
                <div v-else class="d-flex justify-center">
                    <v-progress-circular indeterminate color="mainNew"></v-progress-circular>
                </div>

            </div>

            <div v-if="showBack && channelConnector" class="actions">
                <div class="left-actions">
                    <button type="button" class="back-btn" @click="onBack">
                        <v-icon style="margin-top: 2px;" size="14">mdi-arrow-left</v-icon>
                        Назад
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, ref, onMounted, getCurrentInstance } from 'vue'
import { useProject } from '@root/src/hooks/useProject'
import useRouter from '@/hooks/useRouter'
import ChannelSettingsForm from '@/components/channels/ChannelSettingsForm.vue'
import { CHANNEL_TYPES, PROVIDER_TYPES, CONNECTOR_TYPES } from '@/vars/general'
import { createChannel } from '@/api/networkFixer'
import { fetchConnector } from '@/api/projects'
import { refreshProjectAndAssignChannelSlots } from '@/utils/onboardingHelpers'

export default {
    name: 'TelegramChannelConnectionViewOnboarding',
    components: {
        ChannelSettingsForm,
    },
    props: {
        channelType: {
            type: String,
            required: false,
            validator: (value) => {
                if (!value) return true
                return Object.values(CHANNEL_TYPES).includes(value)
            },
        },
        redirectOnConnect: {
            type: Boolean,
            default: true,
        },
        showBack: {
            type: Boolean,
            default: false,
        },
        embedded: {
            type: Boolean,
            default: false,
        },
        providerTypeOverride: {
            type: String,
            required: false,
        },
    },
    emits: ['channel-connected', 'refresh-step', 'back', 'connector-created'],
    setup(props, { emit }) {
        const root = getCurrentInstance().proxy
        const { project } = useProject()
        const router = useRouter()
        const channelConnector = ref(null)
        const channelSettings = ref({})
        const isTransitioning = ref(false)

        const currentChannelType = computed(() => {
            if (props.channelType) return props.channelType
            const route = router.router.currentRoute
            return route.query.channelType || null
        })

        const providerType = computed(() => {
            if (props.providerTypeOverride) return props.providerTypeOverride
            if (!currentChannelType.value) return null

            const channelToProviderMap = {
                [CHANNEL_TYPES.whatsappChannel]: PROVIDER_TYPES.whatsappDh,
                [CHANNEL_TYPES.telegramChannel]: PROVIDER_TYPES.telegramDh,
                [CHANNEL_TYPES.maxChannel]: PROVIDER_TYPES.maxDh,
                [CHANNEL_TYPES.maxBusiness]: PROVIDER_TYPES.maxBusiness,
            }

            return channelToProviderMap[currentChannelType.value] || null
        })

        const onBack = async () => {
            await root.$store.dispatch('project/deleteConnector', {
                connectorId: channelConnector.value?.connectorId,
                skipLoading: true,
            })
            emit('back')
        }

        onMounted(async () => {
            const channelType = currentChannelType.value

            if (!channelType) {
                root.$store.dispatch('callNotify', 'Ошибка: тип канала не указан')
                return
            }

            const existingChannel = project.value.connectors.find((c) => {
                if (!c) return false
                if (c.connectorType !== channelType) return false
                if (channelType === CHANNEL_TYPES.telegramChannel && props.providerTypeOverride) {
                    return c.providerType === props.providerTypeOverride
                }
                return true
            })

            if (existingChannel) {
                channelConnector.value = existingChannel
                root.$store.commit('project/setLoggedAddingConnector', existingChannel)
            } else {
                const connectorObj = await root.$store.dispatch('project/createConnector', {
                    projectId: project.value.id,
                    connectorType: channelType,
                    providerType: providerType.value,
                })

                sessionStorage.setItem(
                    'currentAddingConnector',
                    JSON.stringify({
                        type: channelType,
                        name: getChannelName(channelType),
                        maxCount: 1,
                    }),
                )

                const res = await root.$store.dispatch('project/addConnector', {
                    connector: connectorObj,
                    skipLoading: true,
                })
                if (res.error) {
                    root.$store.dispatch('callNotify', 'Ошибка создания канала')
                    return
                }

                const connectorId = connectorObj.connectorId
                emit('connector-created', connectorId)
                let updatedConnector = connectorObj

                const channelRes = await createChannel({ connectorId })
                if (!channelRes.error && channelRes.data) {
                    try {
                        const { data: fetchedConnector } = await fetchConnector(connectorId)
                        if (fetchedConnector) {
                            root.$store.commit('updateConnector', fetchedConnector)
                            updatedConnector = fetchedConnector
                        }
                    } catch (e) {
                    }
                }

                await root.$store.dispatch('project/fetchProject', project.value.id)
                const updatedProject = root.$store.getters.projectById(project.value.id)
                const connectorFromProject = updatedProject?.connectors?.find(
                    (c) => c.connectorId === connectorId,
                )
                channelConnector.value = connectorFromProject || updatedConnector
                root.$store.commit('project/setLoggedAddingConnector', channelConnector.value)
            }
        })

        const getChannelName = (channelType) => {
            const names = {
                [CHANNEL_TYPES.whatsappChannel]: 'WhatsApp',
                [CHANNEL_TYPES.telegramChannel]: 'Telegram',
                [CHANNEL_TYPES.maxChannel]: 'MAX',
                [CHANNEL_TYPES.maxBusiness]: 'MAX Business',
            }
            return names[channelType] || channelType
        }

        const onChannelConnected = async () => {
            if (!props.redirectOnConnect) {
                isTransitioning.value = true
            }

            const projectId = project.value?.id
            if (projectId) {
                await refreshProjectAndAssignChannelSlots({
                    store: root.$store,
                    projectId,
                    extraConnectors: channelConnector.value ? [channelConnector.value] : [],
                })
            }

            if (props.redirectOnConnect) {
                try {
                    const connectors = project.value?.connectors || []
                    const whatsappBasis = connectors.find((c) => c && c.connectorType === CONNECTOR_TYPES.whatsappBasis)
                    if (whatsappBasis && channelConnector.value) {
                        const channelId = channelConnector.value.channelId || channelConnector.value.connectorId
                        if (channelId) {
                            await root.$store.dispatch('project/updateConnectorSettings', {
                                connectorId: whatsappBasis.connectorId,
                                settings: {
                                    defaultChannel: { type: 'channel', id: channelId },
                                    yclientsChannel: { type: 'channel', id: channelId },
                                },
                            })
                        }
                    }
                } catch (e) {
                }

                router.router.push(`/project/${project.value.id}?showCascade=true`)
            }

            emit('channel-connected')
            emit('refresh-step')
        }

        return {
            channelConnector,
            channelSettings,
            providerType,
            onChannelConnected,
            isTransitioning,
            onBack,
        }
    },
}
</script>

<style lang="sass" scoped>
@import '@/vars.sass'

.content-wrapper
    margin-top: 0
    .content
        align-items: flex-start
        justify-content: center
        display: flex
        flex-direction: column
        width: 100%
        max-width: 100%
        min-width: 0
        box-sizing: border-box

.connect-panel
    width: 100%
    max-width: 100%
    min-width: 0
    border: 1px solid rgba(229, 232, 242, 0.94)
    border-radius: 24px
    background: #fff
    box-shadow: 0 16px 42px rgba(26, 26, 46, 0.07)
    padding: 28px
    min-height: 380px
    box-sizing: border-box

.section-title
    display: flex
    align-items: flex-start
    justify-content: space-between
    gap: 16px
    margin-bottom: 24px
    min-width: 0
    max-width: 100%

    > div
        min-width: 0
        max-width: 100%

    h2
        font-size: 24px
        letter-spacing: -0.045em
        line-height: 1.12
        margin: 0
        font-weight: 800
        color: $dark-new !important
        overflow-wrap: anywhere

    p
        margin: 6px 0 0
        color: #667085
        font-size: 13px
        max-width: 100%
        line-height: 1.45

.eyebrow
    display: inline-flex
    align-items: center
    gap: 8px
    color: $main-new !important
    font-size: 11px
    font-weight: 900
    text-transform: uppercase
    letter-spacing: 0.12em
    margin-bottom: 10px

.badge
    display: inline-flex
    align-items: center
    gap: 6px
    height: 28px
    border-radius: 999px
    padding: 0 10px
    background: $bcg-card-new
    color: $main-new !important
    font-size: 11px
    font-weight: 800
    white-space: nowrap
    flex: 0 0 auto

.actions
    display: flex
    align-items: center
    justify-content: space-between
    gap: 16px
    margin-top: 16px

.left-actions
    display: flex
    align-items: center
    gap: 12px

.back-btn
    height: 42px
    border: 0
    border-radius: 12px
    padding: 0 18px
    font-size: 13px
    font-weight: 900
    display: inline-flex
    align-items: center
    justify-content: center
    white-space: nowrap
    cursor: pointer
    background: transparent
    color: #596174
    gap: 2px

    ::v-deep .v-icon
        color: inherit

    &:hover
        color: $dark-new
</style>
